import { Theme } from '@mui/material';
import { useCallback, useContext, useState } from 'react';

import { DashboardContext } from '@/Contexts/DashboardContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import { useApplicationUpdateModalData } from '@/hooks/useApplicationUpdateModal';
import { ApplicationDataGridDetailsProps, ApplicationProps } from '@/models/Application';
import { formatErrorMessage } from '@/utils/errorHandlerUtil';

import { ApplicationUpdateDialog } from './ApplicationUpdateDialog';
import { updateDashboardLists } from './utils/updateDashboardLists';

type Props = {
  applicationUpdateModalOpen: boolean;
  applicationDataRow: ApplicationDataGridDetailsProps;
  theme: Theme;
  onModalClose: () => void;
  handleApplicationUpdate: (row: ApplicationDataGridDetailsProps) => void;
};

export default function ApplicationUpdateModal(props: Props) {
  const {
    applicationUpdateModalOpen,
    applicationDataRow,
    theme,
    onModalClose,
    handleApplicationUpdate,
  } = props;

  const { userType } = useContext(UsersContext);
  const { setAlert } = useContext(SnackbarContext);
  const dashboard = useContext(DashboardContext);

  const { updateApplication, getSingleAppForApplicantDashboard } = useApplication();

  const {
    applicationData,
    formData,
    setPartnerId,
    setContactId,
    setStatus,
    setAdditionalStickers,

    partnerOptions,
    contactOptions,
    statusOptions,
    loading,

    resetAll,
  } = useApplicationUpdateModalData({
    open: applicationUpdateModalOpen,
    applicationId: applicationDataRow?.applicationId,
    currentRowStatus: applicationDataRow?.status,
  });

  const [posting, setPosting] = useState(false);

  const handleClose = useCallback(() => {
    resetAll();
    onModalClose();
  }, [resetAll, onModalClose]);

  const onSubmit = useCallback(async () => {
    if (applicationData == null) return;

    const appId = applicationData.id;

    const partnerChanged =
      formData.partnerId != null && formData.partnerId !== applicationData.partnerId;

    const contactChanged =
      formData.contactId != null && formData.contactId !== applicationData.applicationContactId;

    const statusChanged = formData.status != null && formData.status !== applicationData.status;

    // Decide desired behavior:
    // - If null means "user didn't touch it", treat null as no change
    // - If you want to allow clearing, tell me and I'll adjust.
    const stickersChanged =
      formData.additionalStickers != null &&
      formData.additionalStickers !== (applicationData.additionalStickers ?? null);

    const shouldPost = partnerChanged || contactChanged || statusChanged || stickersChanged;

    if (!shouldPost) {
      setAlert({ severity: 'info', message: 'No changes to save.' });
      return;
    }

    const payload: ApplicationProps = {
      ...applicationData,
      partnerId: partnerChanged ? formData.partnerId! : applicationData.partnerId,
      applicationContactId: contactChanged
        ? formData.contactId!
        : applicationData.applicationContactId,
      status: statusChanged ? formData.status! : applicationData.status,
      additionalStickers: stickersChanged
        ? formData.additionalStickers
        : applicationData.additionalStickers,
    };

    try {
      setPosting(true);

      const result = await updateApplication(payload);
      if (result?.id == null) {
        throw new Error(`Update failed for application ${appId}`);
      }

      const updatedGridRow = await getSingleAppForApplicantDashboard(result.id);
      if (updatedGridRow == null) {
        throw new Error(`Could not reload application ${appId}`);
      }

      const updatedDetailsRow: ApplicationDataGridDetailsProps = {
        ...applicationDataRow,
        ...updatedGridRow,
      };

      handleApplicationUpdate(updatedDetailsRow);

      updateDashboardLists({
        updatedApp: updatedGridRow,
        dashboard,
        userType,
      });

      setAlert({
        severity: 'success',
        message: `Application with ID ${appId} updated successfully`,
      });

      handleClose();
    } catch (err) {
      setAlert({
        severity: 'error',
        message: formatErrorMessage(err),
      });
    } finally {
      setPosting(false);
    }
  }, [
    applicationData,
    applicationDataRow,
    formData,
    updateApplication,
    getSingleAppForApplicantDashboard,
    handleApplicationUpdate,
    dashboard,
    userType,
    setAlert,
    handleClose,
  ]);

  return (
    <ApplicationUpdateDialog
      open={applicationUpdateModalOpen}
      onClose={handleClose}
      formData={formData}
      theme={theme}
      userType={userType}
      applicationDataRow={applicationDataRow}
      onPartnerChange={setPartnerId}
      onContactChange={setContactId}
      onStatusChange={setStatus}
      onAdditionalStickersChange={setAdditionalStickers}
      partnerOptions={partnerOptions}
      contactOptions={contactOptions}
      statusOptions={statusOptions}
      loading={loading}
      posting={posting}
      onSubmit={onSubmit}
    />
  );
}
